import type { SearchCriteria } from '@corratech/pylot-filters-and-sort'
import type {
    ConfigurableProductOptions,
    ConfigurableProductOptionsValues,
    Maybe
} from '@pylot-data/pylotschema'
import type { SelectedOptions } from '../../../types/SelectedOptions'
import type { AvailableValues } from './getAvailableOptionValues'

type urlFilterParamType = {
    [key: string]: string
}

/**
 * Obtains default selected options from configurable product's options and filter parameters.
 * Precedence for selecting options (color option example):
 *      if there's a selected color on PLP, always use that (only for color).
 *      If there is no, check if there's a color filter in the URL and use that.
 *      If there is no, then don't select a color. If there's only one color, select that one.
 * @param configurableOptions product configurable options
 * @param filterParams filter search parameters from URL
 * @param showFirstProductAsDefault
 * @param availableValues labels comprables por atributo (`getAvailableOptionValues`).
 * Desde EVO-245 `configurable_options` trae tambien las opciones sin stock, asi que la
 * seleccion por defecto se calcula solo sobre estas. Si se omite, se consideran todas.
 * @returns default selected options object
 */
export const getDefaultSelectedOptions = (
    configurableOptions: ConfigurableProductOptions[] | undefined,
    filterParams?: SearchCriteria[],
    showFirstProductAsDefault?: boolean,
    availableValues?: AvailableValues
): SelectedOptions => {
    if (!configurableOptions) return {}

    return configurableOptions.reduce<SelectedOptions>((acc, option) => {
        const { attribute_code, values } = option
        const availableForOption = availableValues?.[attribute_code!]
        const selectableValues = availableForOption
            ? values?.filter((v) => availableForOption.includes(v?.label!))
            : values
        const matchingFilterParam = filterParams?.find(
            (param) => param.attribute_code === attribute_code
        )
        let val: string | null

        if (matchingFilterParam) {
            const swatchValue = selectableValues?.find((v) =>
                // `value_index` for magento, while `uid` for shopify
                [v?.value_index?.toString(), v?.uid].includes(
                    matchingFilterParam?.filter_value
                )
            )
            val = swatchValue?.label ?? null
        } else {
            val =
                selectableValues?.length === 1 ||
                (selectableValues?.length && showFirstProductAsDefault)
                    ? selectableValues[0]?.label!
                    : null
        }

        acc[attribute_code!] = val
        return acc
    }, {})
}
